import { useMemo } from "react";
import {
  computeMinSupportSpacing,
  DEFLECTION_OPTIONS,
  getSecondMomentOfArea,
  isLoadSupportFamilyCode,
  JOISTS_SPACING_OPTIONS,
  LIVE_LOAD_OPTIONS,
  type SupportSpacingInput,
} from "../../../http/deckDesigner/formulas";
import type { UnitSystem } from "../../../types";
import type { ConnectorMappingForSettings } from "../../../http/deckDesigner/types/connectorMappings";
import type { ConnectorForSettings } from "../../../http/deckDesigner/types/connectors";
import type { Settings } from "../../../http/deckDesigner/types/settings";
import {
  isSubstructureAvailableFor,
  type SubstructureForSettings,
} from "../../../http/deckDesigner/types/substructures";
import type { SupportFamilyForSettings } from "../../../http/deckDesigner/types/supportFamilies";
import type { SupportForSettings } from "../../../http/deckDesigner/types/supports";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { filterConnectorMappings } from "./connectorMappingFilter";
import { mround } from "./connectorScrewLength";
import { getSlopeDirectionDegrees, isSlopedTerrain, type DeckFootprint, type TerrainSlope } from "./deckOutline";
import { getOptionsByItemId } from "./itemOption";
import {
  calculateMaxSlopeDegrees,
  calculateMaxSlopeRiseMm,
  calculateSlopeAdjustedSupportHeightRangeCm,
  calculateSuggestedSlope,
  calculateValidSupportRange,
  calculateWorstCaseSlopeSupportHeightMm,
  getSupportHeightCheck,
  isStackableSupport,
  type SupportHeightCheck,
  type SupportHeightContext,
} from "./supportHeight";

const FLAT_TERRAIN: TerrainSlope = { slopeDegrees: null, slopeArrow: null };
// Supports along a joist need room to be installed: their spacing must be at least this times their width.
const MIN_SUPPORT_SPACING_TO_WIDTH_RATIO = 1.5;
// The fields a spacing suggestion may change, as bits: among suggestions changing as many fields, the
// lower sum comes first - the section, then the joist spacing. Deflection and live load are requirements,
// so they only change when the section and the joist spacing alone can't make room.
const SECTION_CHANGE = 1;
const JOISTS_SPACING_CHANGE = 2;
const DEFLECTION_CHANGE = 4;
const LIVE_LOAD_CHANGE = 8;

const countChangedFields = (changes: number): number =>
  [SECTION_CHANGE, JOISTS_SPACING_CHANGE, DEFLECTION_CHANGE, LIVE_LOAD_CHANGE].filter((change) => changes & change)
    .length;

const compareRanks = (a: number[], b: number[]): number =>
  a.reduce((result, value, index) => result || value - b[index]!, 0);

// One per JFA/SUP support: the configuration closest to the entered one that gives it room along the
// joists (see computeMinSupportSpacing), which the spacing modal's "Apply" writes into the form.
export type SupportSpacingRow = {
  supportFamily: SupportFamilyForSettings;
  itemCode: string;
  width: number;
  substructure: SubstructureForSettings;
  joistsSpacing: number;
  deflection: number;
  liveLoad: number;
  // The resulting spacing between supports along the joists, in meters.
  supportSpacing: number;
};

// One row per support ITEM (not aggregated by family): a family's brackets can be individually narrow
// enough that the slope makes some unreachable while others stay fine, and min/max-ing across all of a
// family's items would blend an unreachable bracket's numbers into a reachable one's, producing an H
// range that looks valid but isn't actually covered by any single item. Sourced from
// supportsAvailableForMaterialClass (already filtered to the selected substructure material class).
export type SupportCompatibilityRow = {
  supportFamily: SupportFamilyForSettings;
  itemCode: string;
  minHeight: number;
  maxHeight: number;
  // The band of totalHeight (H, in cm) that would make THIS specific item valid given the current
  // stack and slope - lets the user see directly what H to pick instead of just why it fails.
  minTotalHeightCm: number;
  maxTotalHeightCm: number;
  // Whether this exact item is selectable right now - the same check the dropdown itself applies
  // (getSupportHeightCheck), not just "is the current H within the band above", since stackable
  // families (NAG/GRANULO) also need the required height to divide evenly.
  isCompatible: boolean;
  // Same check, but with the slope ignored - lets the empty-state message tell slope apart from
  // other causes (H simply out of range for every product, or a NAG/GRANULO divisibility mismatch
  // that would fail even on flat ground) instead of always blaming the slope.
  isCompatibleIgnoringSlope: boolean;
  // Whether SOME value of H would make this item work at the current slope
  // (minTotalHeightCm <= maxTotalHeightCm).
  isReachable: boolean;
  // NAG/GRANULO on a slope: not selectable at any H (see getSupportHeightCheck), so the modal shows
  // their family locked instead of rows with H/slope suggestions.
  isSlopeLocked: boolean;
  // A complete valid configuration for this item, which the modal's "Apply" writes into the form.
  // H is kept as entered whenever the item covers it on flat terrain, and only the slope is reduced
  // (in the same direction - see calculateSuggestedSlope) as much as needed. Otherwise H moves to the
  // closest LOWER value the item can reach (H is a maximum, so it's never raised), keeping as much
  // slope as the item can absorb. suggestedTotalHeightCm is null when no H up to the current one
  // passes - those rows aren't shown.
  suggestedSlope: number;
  suggestedTotalHeightCm: number | null;
  // How far the suggested H is from the current one (0 when H is kept), used to list the rows
  // closest to what the user entered first. Infinity without an H or when the item can't be suggested.
  distanceFromTotalHeightCm: number;
};

// Disabled only when it fits with none of the candidate connectors (see supportCheckConnectors).
const isSupportDisabledForAll = (
  option: SupportForSettings,
  supportCheckConnectors: (ConnectorForSettings | null)[],
  context: SupportHeightContext,
): boolean => supportCheckConnectors.every((candidate) => getSupportHeightCheck(option, candidate, context).disabled);

const getSupportSuggestion = (
  option: SupportForSettings,
  supportCheckConnectors: (ConnectorForSettings | null)[],
  context: SupportHeightContext,
): Pick<SupportCompatibilityRow, "suggestedSlope" | "suggestedTotalHeightCm"> => {
  const { substructure, boardThickness, secondaryItem, totalHeight, footprint, terrainSlope } = context;
  // For the numbers shown (required h range, compatible H, suggestions): the thinnest candidate,
  // i.e. the one leaving the support the most room - the selected connector once there is one.
  const supportCheckConnector = supportCheckConnectors[0] ?? null;
  // NAG and GRANULO only work at whole multiples of minHeight, so the most rise they can absorb is
  // up to their tallest reachable multiple, not up to maxHeight.
  const isStackable = isStackableSupport(option);
  const tallestSupportHeightMm =
    isStackable && option.minHeight > 0
      ? Math.floor(option.maxHeight / option.minHeight) * option.minHeight
      : option.maxHeight;
  const { minimumDeckHeightMm: fixedElementsHeightMm, totalHeightMm } = calculateValidSupportRange(
    substructure,
    boardThickness,
    null,
    secondaryItem,
    supportCheckConnector,
    totalHeight,
  );
  const currentSupportHeightMm = totalHeightMm == null ? null : totalHeightMm - fixedElementsHeightMm;
  const keepsTotalHeight =
    currentSupportHeightMm != null &&
    option.minHeight <= currentSupportHeightMm &&
    currentSupportHeightMm <= tallestSupportHeightMm &&
    (!isStackable || currentSupportHeightMm % option.minHeight === 0);
  // Stackable families are flat-terrain only (see getSupportHeightCheck), so they never absorb any rise.
  const allowedRiseMm = isStackable
    ? 0
    : (keepsTotalHeight ? currentSupportHeightMm : tallestSupportHeightMm) - option.minHeight;
  const suggestedTerrainSlope: TerrainSlope = {
    slopeDegrees: calculateSuggestedSlope(footprint, terrainSlope, allowedRiseMm, context.maxRiseMm),
    slopeArrow: terrainSlope.slopeArrow,
  };
  // The form's own terrain when the slope is kept, so its rise is known already.
  const suggestedMaxRiseMm =
    suggestedTerrainSlope.slopeDegrees === terrainSlope.slopeDegrees
      ? context.maxRiseMm
      : calculateMaxSlopeRiseMm(footprint, suggestedTerrainSlope);

  // When H can't be kept: stackable families take the tallest whole multiple of minHeight not above
  // H, continuous ones the closest H not above the current one inside the band valid at the
  // suggested slope. Rounded to H's 2 decimals, then double-checked with the dropdown's own rule so
  // "Apply" can never write an invalid H (which also rejects a candidate below the band).
  let candidateTotalHeightCm: number;
  if (keepsTotalHeight) {
    candidateTotalHeightCm = totalHeight!;
  } else if (isStackable) {
    const maxUnits = Math.floor(option.maxHeight / option.minHeight);
    const units =
      totalHeightMm == null
        ? 1
        : Math.min(Math.floor((totalHeightMm - fixedElementsHeightMm) / option.minHeight), maxUnits);
    candidateTotalHeightCm = Math.round(((fixedElementsHeightMm + units * option.minHeight) / 10) * 100) / 100;
  } else {
    const { minTotalHeightCm, maxTotalHeightCm } = calculateSlopeAdjustedSupportHeightRangeCm(
      substructure,
      boardThickness,
      secondaryItem,
      supportCheckConnector,
      option.minHeight,
      option.maxHeight,
      footprint,
      suggestedTerrainSlope,
      suggestedMaxRiseMm,
    );
    candidateTotalHeightCm = Math.min(
      Math.max(totalHeight ?? (minTotalHeightCm + maxTotalHeightCm) / 2, Math.ceil(minTotalHeightCm * 100) / 100),
      Math.floor(maxTotalHeightCm * 100) / 100,
      totalHeight ?? Infinity,
    );
  }
  const isCandidateValid = !isSupportDisabledForAll(option, supportCheckConnectors, {
    ...context,
    totalHeight: candidateTotalHeightCm,
    terrainSlope: suggestedTerrainSlope,
    maxRiseMm: suggestedMaxRiseMm,
  });

  return {
    suggestedSlope: suggestedTerrainSlope.slopeDegrees ?? 0,
    suggestedTotalHeightCm: isCandidateValid ? candidateTotalHeightCm : null,
  };
};

type SupportSpacingRowsParams = {
  supportSpacingInput: Omit<SupportSpacingInput, "support">;
  supportsAvailableForMaterialClass: SupportForSettings[] | undefined;
  substructures: SubstructureForSettings[] | undefined;
  substructureMaterialCode: string | undefined;
  unitSystem: UnitSystem | null;
  supportCheckConnectors: (ConnectorForSettings | null)[];
  supportHeightContext: SupportHeightContext;
};

// The spacing modal's rows, best first (see SupportSpacingRow).
export const computeSupportSpacingRows = ({
  supportSpacingInput,
  supportsAvailableForMaterialClass,
  substructures,
  substructureMaterialCode,
  unitSystem,
  supportCheckConnectors,
  supportHeightContext,
}: SupportSpacingRowsParams): SupportSpacingRow[] => {
  const { substructure: currentSubstructure, joistsSpacing, deflection, liveLoad } = supportSpacingInput;

  if (
    !supportsAvailableForMaterialClass ||
    !currentSubstructure ||
    joistsSpacing == null ||
    deflection == null ||
    liveLoad == null
  ) {
    return [];
  }

  // The sections the substructure select offers (same material and market), the stiffest last, so
  // their index distance tells how big a change is.
  const sections = [
    currentSubstructure,
    ...(substructures?.filter(
      (option) =>
        option.id !== currentSubstructure.id &&
        !option.onlyForCalculation &&
        (!substructureMaterialCode || option.substructureMaterials.some((m) => m.code === substructureMaterialCode)) &&
        (!unitSystem || isSubstructureAvailableFor(option, unitSystem)),
    ) ?? []),
  ].sort((a, b) => (getSecondMomentOfArea(a) ?? Infinity) - (getSecondMomentOfArea(b) ?? Infinity));
  const sectionIndex = sections.indexOf(currentSubstructure);
  const joistsSpacingIndex = JOISTS_SPACING_OPTIONS.findIndex((option) => option.value === joistsSpacing);
  // Only the values that widen the spacing: a looser deflection limit, a lower live load.
  const deflections = DEFLECTION_OPTIONS.map((option): number => option.value).filter((value) => value <= deflection);
  const liveLoads = LIVE_LOAD_OPTIONS.map((option): number => option.value).filter((value) => value <= liveLoad);
  // The spacing depends on the family, not on the support (see computeMinSupportSpacing).
  const spacingByKey = new Map<string, number | null>();

  return supportsAvailableForMaterialClass
    .filter((option) => isLoadSupportFamilyCode(option.supportFamily.code))
    .flatMap((option) => {
      const requiredSpacing = (MIN_SUPPORT_SPACING_TO_WIDTH_RATIO * option.width) / 1000;
      // Not narrowed to null: it's only assigned inside the loops' callbacks.
      let best = null as { row: SupportSpacingRow; rank: number[] } | null;

      sections.forEach((section, candidateSectionIndex) => {
        // The section's height changes the support's (h), which must still fit.
        const fitsHeight = supportCheckConnectors.some(
          (candidate) =>
            !getSupportHeightCheck(option, candidate, { ...supportHeightContext, substructure: section }).disabled,
        );

        if (!fitsHeight) {
          return;
        }

        JOISTS_SPACING_OPTIONS.forEach(({ value: candidateJoistsSpacing }, candidateJoistsSpacingIndex) =>
          deflections.forEach((candidateDeflection) =>
            liveLoads.forEach((candidateLiveLoad) => {
              const key = [
                option.supportFamily.code,
                section.id,
                candidateJoistsSpacing,
                candidateDeflection,
                candidateLiveLoad,
              ].join("|");

              if (!spacingByKey.has(key)) {
                spacingByKey.set(
                  key,
                  computeMinSupportSpacing({
                    ...supportSpacingInput,
                    substructure: section,
                    joistsSpacing: candidateJoistsSpacing,
                    deflection: candidateDeflection,
                    liveLoad: candidateLiveLoad,
                    support: option,
                  }),
                );
              }

              const supportSpacing = spacingByKey.get(key);

              if (supportSpacing == null || supportSpacing < requiredSpacing) {
                return;
              }

              const changes =
                (section !== currentSubstructure ? SECTION_CHANGE : 0) |
                (candidateJoistsSpacing !== joistsSpacing ? JOISTS_SPACING_CHANGE : 0) |
                (candidateDeflection !== deflection ? DEFLECTION_CHANGE : 0) |
                (candidateLiveLoad !== liveLoad ? LIVE_LOAD_CHANGE : 0);
              // Requirements kept first, then the fewest fields changed, the section before the joist
              // spacing, and the smallest steps through the selects' options.
              const rank = [
                changes >= DEFLECTION_CHANGE ? 1 : 0,
                countChangedFields(changes),
                changes,
                Math.abs(candidateSectionIndex - sectionIndex) +
                  Math.abs(candidateJoistsSpacingIndex - joistsSpacingIndex) +
                  deflections.indexOf(deflection) -
                  deflections.indexOf(candidateDeflection) +
                  liveLoads.indexOf(liveLoad) -
                  liveLoads.indexOf(candidateLiveLoad),
              ];

              if (!best || compareRanks(rank, best.rank) < 0) {
                best = {
                  rank,
                  row: {
                    supportFamily: option.supportFamily,
                    itemCode: option.item.code,
                    width: option.width,
                    substructure: section,
                    joistsSpacing: candidateJoistsSpacing,
                    deflection: candidateDeflection,
                    liveLoad: candidateLiveLoad,
                    supportSpacing,
                  },
                };
              }
            }),
          ),
        );
      });

      return best ? [best] : [];
    })
    .sort((a, b) => compareRanks(a.rank, b.rank))
    .map(({ row }) => row);
};

type SupportOptionsParams = Pick<
  DeckDesignerConfigurationFormValues,
  | "supportFamily"
  | "support"
  | "substructureMaterial"
  | "substructureMaterialClass"
  | "substructure"
  | "boardThickness"
  | "secondaryItem"
  | "totalHeight"
> & {
  settings: Settings | undefined;
  footprint: DeckFootprint;
  terrainSlope: TerrainSlope;
  supportSpacingInput: Omit<SupportSpacingInput, "support">;
  unitSystem: UnitSystem | null;
  // From useConnectorOptions.
  connectorMappingsForSelectedConfiguration: ConnectorMappingForSettings[] | null | undefined;
  supportCheckConnectors: (ConnectorForSettings | null)[];
  // The compatibility rows are only built while something shows them: this modal, or the "no support
  // family fits the height" message.
  isCompatibilityModalOpen: boolean;
  // The spacing suggestions are only built while their modal shows them.
  isSpacingCompatibilityModalOpen: boolean;
};

// The support side of ConnectorsCard: which support families, supports and support screws the
// current configuration offers, why a list is empty when it is, and the support compatibility rows.
export const useSupportOptions = ({
  settings,
  supportFamily,
  support,
  substructureMaterial,
  substructureMaterialClass,
  substructure,
  boardThickness,
  secondaryItem,
  totalHeight,
  footprint,
  terrainSlope,
  supportSpacingInput,
  unitSystem,
  connectorMappingsForSelectedConfiguration,
  supportCheckConnectors,
  isCompatibilityModalOpen,
  isSpacingCompatibilityModalOpen,
}: SupportOptionsParams) => {
  const supports = settings?.supports;
  const screws = settings?.screws;
  const substructureMaterialCode = substructureMaterial?.code;

  // Not sent separately: each support carries its full family, so the families are the distinct ones of
  // the supports, in order of first appearance - the server sorts the supports by family order first.
  const supportFamilyByCode = useMemo(
    () => new Map(supports?.map((option) => [option.supportFamily.code, option.supportFamily])),
    [supports],
  );
  const supportFamilies = useMemo(() => supports && [...supportFamilyByCode.values()], [supportFamilyByCode, supports]);
  const supportById = useMemo(() => getOptionsByItemId(supports), [supports]);

  const selectedSupport = support ? (supportById.get(support.item.id) ?? support) : support;

  const selectedSupportFamily = useMemo(() => {
    const supportFamilyCode = supportFamily?.code ?? selectedSupport?.supportFamily.code;

    if (!supportFamilyCode) {
      return null;
    }

    return supportFamilyByCode.get(supportFamilyCode) ?? selectedSupport?.supportFamily ?? supportFamily ?? null;
  }, [selectedSupport, supportFamily, supportFamilyByCode]);

  const supportScrewFamiliesForMaterial = useMemo(() => {
    const screwFamilies =
      selectedSupportFamily?.screwFamilies ?? supportFamilies?.flatMap((family) => family.screwFamilies);

    if (!screwFamilies) {
      return undefined;
    }

    if (!substructureMaterialCode) {
      return screwFamilies;
    }

    return screwFamilies.filter((screwFamily) =>
      screwFamily.substructureMaterials.some((m) => m.code === substructureMaterialCode),
    );
  }, [selectedSupportFamily, substructureMaterialCode, supportFamilies]);

  const shouldShowSupportScrew = !!support && (supportScrewFamiliesForMaterial?.length ?? 0) > 0;

  // With a secondary item, a material class only qualifies when a connector mapping of the selected
  // configuration covers both its substructure material and that secondary item. undefined = no
  // restriction (no secondary item, or settings not loaded yet).
  const secondaryItemSubstructureMaterialCodes = useMemo(() => {
    if (!secondaryItem || connectorMappingsForSelectedConfiguration === undefined) {
      return undefined;
    }

    return new Set(
      filterConnectorMappings(connectorMappingsForSelectedConfiguration ?? [], { secondaryItem }).flatMap((mapping) =>
        mapping.substructureMaterials.map((material) => material.code),
      ),
    );
  }, [connectorMappingsForSelectedConfiguration, secondaryItem]);

  const supportsAvailableForMaterialClass = useMemo(
    () =>
      supports?.filter(
        (option) =>
          !substructureMaterialClass ||
          option.materialClasses.some(
            (materialClass) =>
              materialClass.id === substructureMaterialClass.id &&
              (!secondaryItemSubstructureMaterialCodes ||
                secondaryItemSubstructureMaterialCodes.has(materialClass.substructureMaterial.code)),
          ),
      ),
    [secondaryItemSubstructureMaterialCodes, substructureMaterialClass, supports],
  );

  // The same for every support, so computed once here instead of once per support and connector.
  const maxRiseMm = useMemo(() => calculateMaxSlopeRiseMm(footprint, terrainSlope), [footprint, terrainSlope]);

  const supportHeightContext = useMemo(
    (): SupportHeightContext => ({
      substructure,
      boardThickness,
      secondaryItem,
      totalHeight,
      footprint,
      terrainSlope,
      maxRiseMm,
    }),
    [boardThickness, footprint, maxRiseMm, secondaryItem, substructure, terrainSlope, totalHeight],
  );

  // The height check of every support, once: the family picker, the support select and the
  // compatibility rows all read it from here. Disabled only when it fits with none of the candidate
  // connectors (see supportCheckConnectors).
  const supportHeightCheckById = useMemo(() => {
    const checks = new Map<string, SupportHeightCheck>();

    supportsAvailableForMaterialClass?.forEach((option) => {
      const candidateChecks = supportCheckConnectors.map((candidate) =>
        getSupportHeightCheck(option, candidate, supportHeightContext),
      );

      checks.set(option.item.id, {
        disabled: candidateChecks.every((check) => check.disabled),
        disabledIgnoringSlope: candidateChecks.every((check) => check.disabledIgnoringSlope),
      });
    });

    return checks;
  }, [supportCheckConnectors, supportHeightContext, supportsAvailableForMaterialClass]);

  // The supports too wide for the spacing along the joists. That spacing is the same for every support
  // of a family, so it's computed once per family; NAG/GRANULO have none (see computeMinSupportSpacing).
  const tooWideSupportIds = useMemo(() => {
    const spacingByFamilyCode = new Map<string, number | null>();
    const ids = new Set<string>();

    supportsAvailableForMaterialClass?.forEach((option) => {
      const familyCode = option.supportFamily.code;

      if (!spacingByFamilyCode.has(familyCode)) {
        spacingByFamilyCode.set(familyCode, computeMinSupportSpacing({ ...supportSpacingInput, support: option }));
      }

      const spacingMeters = spacingByFamilyCode.get(familyCode);

      if (spacingMeters != null && spacingMeters * 1000 < MIN_SUPPORT_SPACING_TO_WIDTH_RATIO * option.width) {
        ids.add(option.item.id);
      }
    });

    return ids;
  }, [supportSpacingInput, supportsAvailableForMaterialClass]);

  const supportsAvailableForSubstructure = useMemo(
    () =>
      supportsAvailableForMaterialClass?.filter(
        (option) => !supportFamily || option.supportFamily.code === supportFamily.code,
      ),
    [supportFamily, supportsAvailableForMaterialClass],
  );

  const availableSupportFamilyCodes = useMemo(
    () => new Set(supportsAvailableForMaterialClass?.map((option) => option.supportFamily.code) ?? []),
    [supportsAvailableForMaterialClass],
  );

  const supportsFittingHeight = useMemo(
    () => supportsAvailableForMaterialClass?.filter((option) => !supportHeightCheckById.get(option.item.id)?.disabled),
    [supportHeightCheckById, supportsAvailableForMaterialClass],
  );

  // The families with at least one support that fits the height and the spacing.
  const validSupportFamilyCodes = useMemo(
    () =>
      new Set(
        supportsFittingHeight
          ?.filter((option) => !tooWideSupportIds.has(option.item.id))
          .map((option) => option.supportFamily.code) ?? [],
      ),
    [supportsFittingHeight, tooWideSupportIds],
  );

  const supportFamiliesAvailableForContext = useMemo(() => {
    if (!supportsAvailableForMaterialClass) {
      return supportFamilies;
    }

    return supportFamilies?.filter((option) => availableSupportFamilyCodes.has(option.code));
  }, [availableSupportFamilyCodes, supportFamilies, supportsAvailableForMaterialClass]);

  const filteredSupportFamilies = useMemo(() => {
    if (!supportsAvailableForMaterialClass) {
      return supportFamiliesAvailableForContext;
    }

    return supportFamiliesAvailableForContext?.filter((option) => validSupportFamilyCodes.has(option.code));
  }, [supportFamiliesAvailableForContext, supportsAvailableForMaterialClass, validSupportFamilyCodes]);

  // Mirrors hasNoConnectorFamilyMapping - no support family at all covers the selected substructure
  // material class, independent of height.
  const hasNoSupportFamilyMapping =
    !!supportFamilies?.length &&
    !!supportFamiliesAvailableForContext &&
    supportFamiliesAvailableForContext.length === 0;

  // Material-class-fitting families exist, but none of them survive the height filter - mirrors
  // hasNoConnectorFamilyFittingHeight. The total height field itself carries the real, actionable
  // error (with the exact valid range), so this is just an info explaining why the grid is empty.
  const hasNoSupportFamilyFittingHeight =
    !hasNoSupportFamilyMapping && !!supportFamiliesAvailableForContext?.length && supportsFittingHeight?.length === 0;

  // Supports fit the height, but every one is too wide for the spacing along the joists: a smaller joist
  // spacing lowers the load on each joist, so the supports can be spaced wider.
  const hasNoSupportFamilyFittingSpacing =
    !hasNoSupportFamilyMapping &&
    !!supportsFittingHeight?.length &&
    !!filteredSupportFamilies &&
    filteredSupportFamilies.length === 0;

  const filteredSupports = useMemo(
    () =>
      supportsAvailableForSubstructure?.filter(
        (option) => !supportHeightCheckById.get(option.item.id)?.disabled && !tooWideSupportIds.has(option.item.id),
      ),
    [supportHeightCheckById, supportsAvailableForSubstructure, tooWideSupportIds],
  );

  const filteredSupportById = useMemo(() => getOptionsByItemId(filteredSupports), [filteredSupports]);

  const minSupportScrewLength = useMemo(
    () => mround(Math.max(4 * 4.5, (substructure?.base ?? 0) / 2), 5),
    [substructure?.base],
  );

  const maxSupportScrewLength = substructure?.base ?? undefined;

  const filteredSupportScrews = useMemo(() => {
    if (!supportScrewFamiliesForMaterial) {
      return undefined;
    }

    const screwFamilyIds = new Set(supportScrewFamiliesForMaterial.map((screwFamily) => screwFamily.id));

    return screws?.filter(
      (option) =>
        screwFamilyIds.has(option.screwFamily.id) &&
        option.length >= minSupportScrewLength &&
        (maxSupportScrewLength == null || option.length <= maxSupportScrewLength),
    );
  }, [maxSupportScrewLength, minSupportScrewLength, screws, supportScrewFamiliesForMaterial]);

  const filteredSupportScrewById = useMemo(() => getOptionsByItemId(filteredSupportScrews), [filteredSupportScrews]);

  const hasNoCompatibleSupportScrew =
    shouldShowSupportScrew && !!filteredSupportScrews && filteredSupportScrews.length === 0;

  const isSupportCompatibilityNeeded = isCompatibilityModalOpen || hasNoSupportFamilyFittingHeight;

  // Reference table behind the "why is this empty" message - same idea as the modal's connector rows,
  // but supports aren't grooving-dependent.
  const supportCompatibilityRows = useMemo((): SupportCompatibilityRow[] => {
    if (!isSupportCompatibilityNeeded || !supportsAvailableForMaterialClass) {
      return [];
    }

    // Too wide for the spacing at any H, so never compatible.
    return (
      supportsAvailableForMaterialClass
        .filter((option) => !tooWideSupportIds.has(option.item.id))
        .map((option): SupportCompatibilityRow => {
          const { minTotalHeightCm, maxTotalHeightCm } = calculateSlopeAdjustedSupportHeightRangeCm(
            substructure,
            boardThickness,
            secondaryItem,
            supportCheckConnectors[0] ?? null,
            option.minHeight,
            option.maxHeight,
            footprint,
            terrainSlope,
            maxRiseMm,
          );
          const heightCheck = supportHeightCheckById.get(option.item.id);
          const isSlopeLocked = isStackableSupport(option) && isSlopedTerrain(terrainSlope);
          const suggestion = getSupportSuggestion(option, supportCheckConnectors, supportHeightContext);

          return {
            supportFamily: option.supportFamily,
            itemCode: option.item.code,
            minHeight: option.minHeight,
            maxHeight: option.maxHeight,
            minTotalHeightCm,
            maxTotalHeightCm,
            isCompatible: !heightCheck?.disabled,
            isCompatibleIgnoringSlope: !heightCheck?.disabledIgnoringSlope,
            isSlopeLocked,
            isReachable: !isSlopeLocked && minTotalHeightCm <= maxTotalHeightCm,
            ...suggestion,
            distanceFromTotalHeightCm:
              totalHeight == null || isSlopeLocked || suggestion.suggestedTotalHeightCm == null
                ? Infinity
                : Math.abs(suggestion.suggestedTotalHeightCm - totalHeight),
          };
        })
        // Closest to the current H first, then the one keeping more slope; families then follow their
        // closest row, since the modal's grouping keeps first-seen order.
        .sort(
          (a, b) =>
            (a.distanceFromTotalHeightCm === b.distanceFromTotalHeightCm
              ? 0
              : a.distanceFromTotalHeightCm - b.distanceFromTotalHeightCm) ||
            b.suggestedSlope - a.suggestedSlope ||
            a.supportFamily.code.localeCompare(b.supportFamily.code) ||
            a.minHeight - b.minHeight,
        )
    );
  }, [
    boardThickness,
    footprint,
    isSupportCompatibilityNeeded,
    maxRiseMm,
    secondaryItem,
    substructure,
    supportCheckConnectors,
    supportHeightCheckById,
    supportHeightContext,
    supportsAvailableForMaterialClass,
    terrainSlope,
    tooWideSupportIds,
    totalHeight,
  ]);

  const supportSpacingRows = useMemo(
    () =>
      isSpacingCompatibilityModalOpen
        ? computeSupportSpacingRows({
            supportSpacingInput,
            supportsAvailableForMaterialClass,
            substructures: settings?.substructures,
            substructureMaterialCode,
            unitSystem,
            supportCheckConnectors,
            supportHeightContext,
          })
        : [],
    [
      isSpacingCompatibilityModalOpen,
      settings?.substructures,
      substructureMaterialCode,
      supportCheckConnectors,
      supportHeightContext,
      supportSpacingInput,
      supportsAvailableForMaterialClass,
      unitSystem,
    ],
  );

  // The support height (h) the configuration needs, from the deck's lowest vertex (the h at H, flat
  // terrain) down to the deck's highest terrain point - same values as the 3D slope view. Only for
  // the message shown when no support family fits the height.
  const requiredSupportHeightRange = useMemo(() => {
    if (!hasNoSupportFamilyFittingHeight) {
      return null;
    }

    const supportCheckConnector = supportCheckConnectors[0] ?? null;
    const requiredSupportHeightMaxMm = calculateWorstCaseSlopeSupportHeightMm(
      substructure,
      boardThickness,
      null,
      secondaryItem,
      supportCheckConnector,
      totalHeight,
      footprint,
      FLAT_TERRAIN,
    );
    const requiredSupportHeightMinMm = calculateWorstCaseSlopeSupportHeightMm(
      substructure,
      boardThickness,
      null,
      secondaryItem,
      supportCheckConnector,
      totalHeight,
      footprint,
      terrainSlope,
      maxRiseMm,
    );

    return requiredSupportHeightMinMm != null && requiredSupportHeightMaxMm != null && requiredSupportHeightMinMm >= 0
      ? { min: Math.round(requiredSupportHeightMinMm), max: Math.round(requiredSupportHeightMaxMm) }
      : null;
  }, [
    boardThickness,
    footprint,
    hasNoSupportFamilyFittingHeight,
    maxRiseMm,
    secondaryItem,
    substructure,
    supportCheckConnectors,
    terrainSlope,
    totalHeight,
  ]);

  // When no support is reachable at any H, the slope alone is the problem (with a flat terrain every
  // support is reachable at some H), so the message says how far to reduce it instead: the widest
  // support's adjustment range is the most rise any support can absorb.
  const supportSlopeDrop = useMemo(() => {
    if (supportCompatibilityRows.some((row) => row.isReachable)) {
      return null;
    }

    const widestSupportRow = supportCompatibilityRows
      .filter((row) => !row.isSlopeLocked)
      .reduce<SupportCompatibilityRow | null>(
        (widest, row) =>
          widest == null || row.maxHeight - row.minHeight > widest.maxHeight - widest.minHeight ? row : widest,
        null,
      );

    if (!widestSupportRow) {
      return null;
    }

    const widestRangeMm = widestSupportRow.maxHeight - widestSupportRow.minHeight;

    return {
      itemCode: widestSupportRow.itemCode,
      widestRangeMm,
      maxSlopeDegrees: calculateMaxSlopeDegrees(
        footprint,
        getSlopeDirectionDegrees(terrainSlope.slopeArrow),
        widestRangeMm,
      ),
    };
  }, [footprint, supportCompatibilityRows, terrainSlope.slopeArrow]);

  return {
    supportFamilyByCode,
    supportFamilies,
    supportsAvailableForMaterialClass,
    filteredSupportFamilies,
    validSupportFamilyCodes,
    hasNoSupportFamilyMapping,
    hasNoSupportFamilyFittingHeight,
    hasNoSupportFamilyFittingSpacing,
    filteredSupports,
    filteredSupportById,
    shouldShowSupportScrew,
    filteredSupportScrews,
    filteredSupportScrewById,
    hasNoCompatibleSupportScrew,
    supportCompatibilityRows,
    supportSpacingRows,
    requiredSupportHeightRange,
    supportSlopeDrop,
  };
};

export type SupportOptions = ReturnType<typeof useSupportOptions>;
